
import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { CommandResult, CommandType } from '@/utils/voiceCommandUtils';
import { VoiceConfig } from '@/utils/voiceUtils';

interface CommandExecutorOptions {
  config: VoiceConfig;
  onResponse: (text: string) => void;
  optionElements?: Record<string, any>;
  // new: allow for ARIA live region text
  onScreenReaderAnnouncement?: (text: string) => void;
}

export const useVoiceCommandExecutor = ({
  config,
  onResponse,
  optionElements,
  onScreenReaderAnnouncement
}: CommandExecutorOptions) => {
  const navigate = useNavigate();
  const location = useLocation();

  const giveFeedback = useCallback((msg: string) => {
    toast(msg);
    // Inform screen reader
    if (onScreenReaderAnnouncement) onScreenReaderAnnouncement(msg);
  }, [onScreenReaderAnnouncement]);

  const executeCommand = useCallback((commandResult: CommandResult) => {
    console.log('Executing command:', commandResult);

    // Always provide voice feedback
    if (commandResult.response) {
      onResponse(commandResult.response);
    }

    // If there's a direct action handler, execute it
    if (commandResult.action) {
      commandResult.action();
      return true;
    }

    // Handle based on command type
    switch (commandResult.type) {
      case 'navigation':
        return handleNavigationCommand(commandResult);

      case 'search':
        return handleSearchCommand(commandResult);

      case 'accessibility':
        return handleAccessibilityCommand(commandResult);

      case 'help':
        // Help command now shows contextual info
        const contextHelp = getContextualHelp(config, location.pathname);
        if (contextHelp) {
          giveFeedback(contextHelp);
        }
        return true;

      default:
        return false;
    }
  }, [config, navigate, location.pathname, onResponse, optionElements, giveFeedback]);

  // Handle navigation commands
  const handleNavigationCommand = useCallback((commandResult: CommandResult) => {
    const { data } = commandResult;

    if (data?.action === 'back') {
      window.history.back();
      giveFeedback('Navigating back');
      return true;
    }
    if (data?.page && config.routes) {
      for (const [routeName, routePath] of Object.entries(config.routes)) {
        if (data.page.toLowerCase().includes(routeName.toLowerCase())) {
          setTimeout(() => {
            if (routePath.startsWith('http')) {
              window.location.href = routePath;
            } else {
              navigate(routePath);
            }
            giveFeedback(`Navigating to ${routeName}`);
          }, 1200);
          return true;
        }
      }
    }
    return false;
  }, [config.routes, navigate, giveFeedback]);

  // Handle search commands
  const handleSearchCommand = useCallback((commandResult: CommandResult) => {
    const { data } = commandResult;
    if (data?.query) {
      toast.info(`Searching for: ${data.query}`);
      giveFeedback(`Searching for ${data.query}`);
      return true;
    }
    return false;
  }, [giveFeedback]);

  // Handle accessibility commands with feedback
  const handleAccessibilityCommand = useCallback((commandResult: CommandResult) => {
    const { data } = commandResult;

    if (!optionElements || !data?.feature) {
      return false;
    }

    let feedbackMsg = '';
    switch (data.feature) {
      case 'darkMode':
        if (optionElements.darkMode && optionElements.darkMode.input) {
          optionElements.darkMode.input.checked = data.value;
          optionElements.darkMode.input.dispatchEvent(new Event('change'));
          feedbackMsg = data.value ? 'Dark mode enabled' : 'Dark mode disabled';
        }
        break;
      case 'fontSize':
        if (optionElements.fontSize && typeof data.action === 'string') {
          // (Assume slider or input - simulate increment/decrement/reset)
          let el = optionElements.fontSize.input;
          if (el) {
            // Simulate increase/decrease/reset via step changes
            let curr = parseInt(el.value || '100', 10);
            let newFont = curr;
            if (data.action === 'increase') newFont = Math.min(curr + 10, 200);
            if (data.action === 'decrease') newFont = Math.max(curr - 10, 80);
            if (data.action === 'reset') newFont = 100;
            el.value = newFont;
            el.dispatchEvent(new Event('change', { bubbles: true }));
            feedbackMsg = data.action === 'reset'
              ? 'Font size reset to default'
              : `Font size ${data.action}d`;
          }
        }
        break;
      case 'highlightLinks':
        if (optionElements.highlightLinks && optionElements.highlightLinks.input) {
          optionElements.highlightLinks.input.checked = data.value;
          optionElements.highlightLinks.input.dispatchEvent(new Event('change'));
          feedbackMsg = data.value ? 'Link highlighting enabled' : 'Link highlighting disabled';
        }
        break;
      case 'largeCursor':
        if (optionElements.largeCursor && optionElements.largeCursor.input) {
          optionElements.largeCursor.input.checked = data.value;
          optionElements.largeCursor.input.dispatchEvent(new Event('change'));
          feedbackMsg = data.value ? 'Large pointer enabled' : 'Large pointer disabled';
        }
        break;
      default:
        feedbackMsg = 'Accessibility feature toggled';
    }
    if (feedbackMsg) giveFeedback(feedbackMsg);
    return !!feedbackMsg;
  }, [optionElements, giveFeedback]);

  // Generates contextual help text based on the route and available options
  function getContextualHelp(config: VoiceConfig, pathname: string): string | null {
    // Only English for now, can be extended later
    const page = Object.entries(config.routes || {}).find(([name, path]) => path === pathname)?.[0];
    let helpText = config.customResponses?.help || "";
    if (page) {
      helpText += `\nOn this page (${page}), try: "increase font size", "enable dark mode", or "highlight links".`;
    } else {
      helpText += '\nTry commands like: "go to home", "subscribe", "turn on dark mode" or "search for accessibility".';
    }
    helpText += '\nFor accessibility settings: "enable large pointer", "center text", or "underline text".';
    return helpText;
  }

  return executeCommand;
};
